// AI Generated Test
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PolicyRepoService } from '@client/app/services/api/policy-repo/policy-repo.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { SessionService } from '@monsido/core/session/session.service';
import { ChangesHistoryService } from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { FormGlobalPolicyComponent } from '@client/app/forms/global-policy/form-global-policy.component';
import { StateService } from '@uirouter/core';

@Component({
    selector: 'domain-dashboard-policies',
    standalone: false,
    template: `
        <div class="card panel-monsido-card max-height d-flex flex-column">
            <a class="p-0" [uiSref]="'base.customer.domain.policy.dashboard'" [attr.aria-label]="'Content Policies' | translate">
                <mon-panel-header [pHeader]="'Content Policies' | translate">
                    <div class="text-right">
                        <div class="btn btn-link">
                            <span class="pl-1 fas fa-chevron-right" aria-hidden="true"></span>
                        </div>
                    </div>
                </mon-panel-header>
            </a>
            <div class="card-body flex-grow-1">
                <mon-spinner [loading]="loading">
                    <div class="row my-3" *ngIf="hasPolicies()">
                        <div class="offset-4 col-18">
                            <mon-chart-doughnut
                                    [labels]="labels"
                                    [data]="data"
                                    [options]="options"
                                    [colors]="colors">
                            </mon-chart-doughnut>
                        </div>
                        <div class="col-26">
                            <div class="row">
                                <div class="col-48 mb-4 px-0">
                                    <notification-change [title]="'Policies with violations' | translate"
                                                         [digit]="policiesWithErrors"
                                                         [miniDigits]="changesSinceLastScan"
                                                         [link]="'base.customer.domain.policy.dashboard'"
                                                         unit="">
                                    </notification-change>
                                </div>
                                <div class="col-48">
                                    <ul class="list-inline">
                                        <li [ngbTooltip]="'Unwanted' | translate" [triggers]="'focus hover'">
                                            <a [uiSref]="'base.customer.domain.policy.policies'" [uiParams]="{tab: 'violation'}"
                                               class="text-black"
                                               [attr.aria-label]="'Go to Policy List' | translate">
                                                <span class="fas fa-ban fa-lg"
                                                      [class]="{'text-danger': violationCount > 0, 'text-grey': violationCount === 0}"
                                                      aria-hidden="true">
                                                </span>
                                                <span class="sr-only" translate>Violation count</span>
                                                <span>{{ violationCount | monNumber }}</span>
                                            </a>
                                        </li>
                                        <li [ngbTooltip]="'Required' | translate" [triggers]="'focus hover'">
                                            <a [uiSref]="'base.customer.domain.policy.policies'" [uiParams]="{tab: 'required'}"
                                               class="text-black"
                                               [attr.aria-label]="'Go to Policy List' | translate">
                                                <span class="fas fa-exclamation-triangle fa-lg"
                                                      aria-hidden="true"
                                                      [class]="{'text-orange': requiredCount > 0, 'text-grey': requiredCount === 0}">
                                                </span>
                                                <span class="sr-only" translate>Required count</span>
                                                <span>{{ requiredCount | monNumber }}</span>
                                            </a>
                                        </li>
                                        <li [ngbTooltip]="'Matches' | translate" [triggers]="'focus hover'">
                                            <a [uiSref]="'base.customer.domain.policy.policies'" [uiParams]="{tab: 'search'}"
                                               class="text-black"
                                               [attr.aria-label]="'Go to Policy List' | translate">
                                                <span class="fas fa-search fa-lg"
                                                      aria-hidden="true"
                                                      [class]="{'text-info': searchCount > 0, 'text-grey': searchCount === 0}">
                                                </span>
                                                <span>{{ searchCount | monNumber }}</span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="max-height d-flex flex-column align-items-center" *ngIf="!hasPolicies()">
                        <div class="text-center">
                            <h3 class="text-grey mt-10 mb-5 font-weight-300 no-policies-msg" translate>No content policies have been created yet</h3>
                            <button type="button" (click)="openPolicy()" class="btn btn-border-radius btn-marine-blue" translate>
                                Click to create your first policy
                            </button>
                        </div>
                    </div>
                </mon-spinner>
            </div>
            <div class="card-footer bg-white text-right" *ngIf="showHistory">
                <a class="btn-link" [uiSref]="'base.customer.domain.changesHistory'" [uiParams]="historyCenterParameters" translate>
                    Show history
                </a>
            </div>
        </div>
    `,
})
export class DomainDashboardPoliciesComponent implements OnInit {
    @Input() historyData: any;
    @Input() totalContentCount: any;
    @Input() linksCount: any;

    loading = false;
    historyCenterParameters: any = {};
    showHistory = false;
    labels: any[] = [[''], ['']];
    colors: string[] = ['#4dc0bd', '#dcdcdc'];
    domain: any;
    policyCategory: any = {};
    pagesErrorsCount = 0;
    policies: any[] = [];
    data: any;
    options: any;
    violationCount = 0;
    requiredCount = 0;
    searchCount = 0;
    policiesWithErrors = 0;
    changesSinceLastScan = 0;

    constructor (
        private pipeService: PipeService,
        private policyRepoService: PolicyRepoService,
        private complianceService: ComplianceService,
        private monEventService: MonEventService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private changesHistoryService: ChangesHistoryService,
        private activeFeatureService: ActiveFeatureService,
        private stateService: StateService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    activate (): void {
        this.policyCategory = {};
        this.pagesErrorsCount = 0;
        this.policies = [];
        this.historyCenterParameters = this.changesHistoryService.getHistoryCenterParameters('policy');
        this.showHistory = this.activeFeatureService.isFeatureActivePure('history_center_all');
        this.labels = [[''], ['']];
        this.colors = ['#4dc0bd', '#dcdcdc'];
        this.domain = this.sessionService.domain;
        this.getPolicies();
    }

    getPolicies (): void {
        const params = {
            page_size: 0,
        };

        const promises = [this.getPolicyPages(), this.policyRepoService.domainGetAll(params)];
        this.loading = true;

        Promise.all(promises).then((values: any[]) => {
            this.pagesErrorsCount = parseInt(values[0].total);
            this.policies = values[1];
            this.setupPolicyCounts(values[1]);
            this.setupDoughnutChart(values[1]);
            this.loading = false;
        }, () => {});
    }

    getPolicyPages (): Promise<any> {
        const params = {
            page_size: 1,
            page: 1,
        };
        return this.policyRepoService.domainGetPages(params);
    }

    setupPolicyCounts (policies: any[]): void {
        this.violationCount = policies.reduce((num, policy) => {
            if (policy.matches_count > 0 && policy.mode === 'violation') {
                num++;
            }
            return num;
        }, 0);

        this.requiredCount = policies.reduce((num, policy) => {
            if (policy.matches_count > 0 && policy.mode === 'required') {
                num++;
            }
            return num;
        }, 0);

        this.searchCount = policies.reduce((num, policy) => {
            if (policy.matches_count > 0 && policy.mode === 'search') {
                num++;
            }
            return num;
        }, 0);

        this.policiesWithErrors = this.violationCount + this.requiredCount + this.searchCount;
    }

    setupDoughnutChart (policies: any[]): void {
        const activePolicies = policies.filter((policy: any) => {
            return policy.state !== 'running';
        });

        this.data = this.complianceService.policyCompliance(
            activePolicies,
            this.totalContentCount,
            this.linksCount,
        );
        this.options = {
            centerText: {
                percentageLabel: this.pipeService.get('monNumber')(
                    this.complianceService.policyCompliancePercentage(
                        activePolicies,
                        this.totalContentCount,
                        this.linksCount,
                    ) / 100,
                    '0.[00] %',
                ),
                totalLabel: this.translateService.getString('overall compliance'),
                subLabel: this.translateService.getString('achieved'),
                totalFontSize: 150,
                subFontSize: 150,
            },
            cutoutPercentage: 80,
            tooltips: {
                enabled: false,
            },
            hover: {
                mode: null,
            },
        };
    }

    openPolicy (): void {
        const params = {
            body: FormGlobalPolicyComponent,
            size: 'fw',
        };
        const callback = (policy: any) => {
            if (policy && typeof policy === 'object') {
                this.getPolicies();
            }
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params, callback });
    }

    hasPolicies (): boolean {
        return this.policies.length > 0;
    }
}
